import React, { ReactNode } from 'react';
import { Alert, Col, Form, Input, Row, Select, Space } from 'antd';
import CcCluster, {
    CcClusterTechnology,
} from '../../../services/models/CcCluster';
import FormItemInlineSwitch from '../../../common/DataEntry/FormItemInlineSwitch';
import ClusterHostDatabasesSelect from '../../Clusters/ClusterHostDatabasesSelect';
import ClusterHostDatabaseTablesSelect from '../../Clusters/ClusterHostDatabaseTablesSelect';
import InfoIcon from '@severalnines/bar-frontend-components/build/lib/General/InfoIcon';
import { MULTI_SELECT_ALL_OPTION_VALUE } from '../../../common/DataEntry/MultiSelect';
import { getNodeHostWithDesc } from '../../Nodes/NodeFormat';
import BackupFormConfigurator, {
    BackupFormFieldsType,
} from '../Config/BackupFormConfigurator';
import { FormInstance } from 'antd/lib/form';
import { UserClusterDatabaseSchema } from '../../Clusters/useClusterDatabases';
import InputNumberWide from '../../../common/DataEntry/InputNumberWide';

const CONFIGURATION_FIELDS: (keyof BackupFormFieldsType)[] = [
    'compression',
    'enableRetention',
];
const TOP_ADVANCED_FIELDS: (keyof BackupFormFieldsType)[] = [
    'dataStreamsAndIndexes',
    'allowPartialIndexes',
    'ignoreUnavailableIndexes',
    'includeGlobalState',
];
const ADVANCED_FIELDS: (keyof BackupFormFieldsType)[] = [
    'backupSystemDatabases',
    'encryptBackup',
    'enablePartial',
    'includeTables',
    'excludeTables',
    'useQPress',
    'extendedInsert',
    'splitDumpFiles',
    'verifyBackup',
    'failoverBackup',
    'usePIGZ',
    'wsrepDeSync',
    'xtrabackupBackupLocks',
    'xtrabackupLockDdlPerTable',
];
const ADVANCED_INPUTS: (keyof BackupFormFieldsType)[] = [
    'xtrabackupParallellism',
    'throttleRateNet',
];

export const PARTIAL_BACKUP_NOT_ALLOWED_SCHEMAS = [
    'performance_schema',
    'information_schema',
    'sys',
    'test',
    'mysql',
];

export default BackupAdvancedForm;

export type BackupAdvancedFormProps = {
    form: FormInstance;
    cluster?: CcCluster;
    configurator: BackupFormConfigurator;
};

function BackupAdvancedForm({
    form,
    cluster,
    configurator,
}: BackupAdvancedFormProps) {
    return (
        <div className="BackupAdvancedForm">
            <Row gutter={[24, 0]}>
                <Col span={24}>
                    <h3>Advanced settings</h3>
                </Col>
            </Row>
            <Form.Item
                shouldUpdate={true} // test this
                noStyle
            >
                {() => [
                    <Row gutter={[25, 0]} wrap key="switches">
                        {TOP_ADVANCED_FIELDS.filter((field) =>
                            configurator.available(field)
                        )
                            .map((field: string) => (
                                <Col xs={25} sm={25} md={12} key={field}>
                                    {getFormField(field)}
                                </Col>
                            ))
                            .filter((item: ReactNode) => !!item) || []}
                        {CONFIGURATION_FIELDS.filter((field) =>
                            configurator.available(field)
                        )
                            .map((field) => (
                                <Col xs={25} sm={25} md={12} key={field}>
                                    {getFormField(field)}
                                </Col>
                            ))
                            .filter((item: ReactNode) => !!item) || []}
                        {ADVANCED_FIELDS.filter((field) =>
                            configurator.available(field)
                        )
                            .map((field) => (
                                <Col xs={25} sm={25} md={12} key={field}>
                                    {getFormField(field)}
                                </Col>
                            ))
                            .filter((item: ReactNode) => !!item) || []}
                    </Row>,
                    <Row gutter={[25, 0]} key="inputs">
                        {ADVANCED_INPUTS.filter((field) =>
                            configurator.available(field)
                        ).map((field: string) => (
                            <Col xs={25} sm={25} md={12} key={field}>
                                {getFormField(field)}
                            </Col>
                        ))}
                    </Row>,
                ]}
            </Form.Item>
        </div>
    );

    function getFormField(field: string) {
        return getBackupAdvancedFormField(field, {
            form,
            configurator,
            cluster,
        });
    }
}

export function getBackupAdvancedFormField(
    field: string,
    {
        form,
        configurator,
        cluster,
    }: {
        form: FormInstance;
        configurator: BackupFormConfigurator;
        cluster?: CcCluster;
    }
) {
    switch (field) {
        case 'compression':
            return (
                <FormItemInlineSwitch
                    justify={true}
                    name="compression"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                    disabled={configurator.disabled(field)}
                    extraOnSwitch={
                        configurator.available('compressionLevel') ? (
                            <Form.Item
                                name="compressionLevel"
                                label={
                                    <Space>
                                        {getBackupAdvancedFormFieldName(
                                            'compressionLevel'
                                        )}
                                    </Space>
                                }
                            >
                                <Select
                                    options={[
                                        { value: 1, label: '1' },
                                        { value: 2, label: '2' },
                                        { value: 3, label: '3' },
                                        { value: 4, label: '4' },
                                        { value: 5, label: '5' },
                                        {
                                            value: 6,
                                            label: '6 (System Default)',
                                        },
                                        { value: 7, label: '7' },
                                        { value: 8, label: '8' },
                                        { value: 9, label: '9' },
                                    ]}
                                />
                            </Form.Item>
                        ) : null
                    }
                />
            );
        case 'enableRetention':
            const defaultRetention = form.getFieldValue('defaultRetention');
            return (
                <FormItemInlineSwitch
                    justify={true}
                    name="enableRetention"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                    extraOnSwitch={
                        <Form.Item
                            name="customRetention"
                            extra={`The default retention period is ${defaultRetention} days`}
                        >
                            <InputNumberWide
                                placeholder={`${defaultRetention} Days`}
                                min={1}
                            />
                        </Form.Item>
                    }
                />
            );
        case 'encryptBackup':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="encryptBackup"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                    extraOnSwitch={
                        <Alert
                            message="Encryption key will be auto-generated"
                            type="info"
                            showIcon={true}
                        />
                    }
                />
            );
        case 'enablePartial':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="enablePartial"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                    extraOnSwitch={
                        <Form.Item
                            name="partialDatabases"
                            label={<Space>Databases</Space>}
                        >
                            <ClusterHostDatabasesSelect
                                clusterId={form.getFieldValue('clusterId')}
                                host={getHostWithPort()}
                                filterDatabases={(
                                    schema: UserClusterDatabaseSchema
                                ) => {
                                    return cluster?.isTechnology(
                                        CcClusterTechnology.TECHNOLOGY_MYSQL
                                    )
                                        ? !PARTIAL_BACKUP_NOT_ALLOWED_SCHEMAS.includes(
                                              schema.name
                                          )
                                        : true;
                                }}
                            />
                        </Form.Item>
                    }
                />
            );
        case 'includeTables':
            const handleIncludeChange = (value: boolean) => {
                const exclude = form.getFieldValue('excludeTables');
                if (value && exclude) {
                    form.setFieldsValue({
                        excludeTables: false,
                    });
                }
            };

            const databases = form.getFieldValue('partialDatabases');
            const includeDisabled =
                !databases ||
                databases.find(
                    (item: string) => item === MULTI_SELECT_ALL_OPTION_VALUE
                );
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="includeTables"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                    onChange={handleIncludeChange}
                    extraOnSwitch={
                        <Form.Item name="partialTables">
                            <ClusterHostDatabaseTablesSelect
                                clusterId={form.getFieldValue('clusterId')}
                                host={getHostWithPort()}
                                disabled={includeDisabled}
                                schemas={includeDisabled ? [] : databases}
                            />
                        </Form.Item>
                    }
                />
            );
        case 'excludeTables':
            const excludeDisabled = configurator.disabled('excludeTables');
            const handleExcludeChange = (value: boolean) => {
                const include = form.getFieldValue('includeTables');
                if (value && include) {
                    form.setFieldsValue({
                        includeTables: false,
                    });
                }
            };
            const handleTableListLoad = (tables: string[]) => {
                form.setFieldsValue({
                    tableList: tables,
                });
            };
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="excludeTables"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                    onChange={handleExcludeChange}
                    extraOnSwitch={
                        <Form.Item name="partialTables">
                            <ClusterHostDatabaseTablesSelect
                                clusterId={form.getFieldValue('clusterId')}
                                host={getHostWithPort()}
                                disabled={excludeDisabled}
                                schemas={
                                    excludeDisabled
                                        ? []
                                        : form.getFieldValue('partialDatabases')
                                }
                                onTableListLoad={handleTableListLoad}
                            />
                        </Form.Item>
                    }
                />
            );
        case 'splitDumpFiles':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="splitDumpFiles"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                />
            );
        case 'failoverBackup':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="failoverBackup"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                    extraOnSwitch={
                        <Form.Item
                            name="failOverHost"
                            label={<Space>Failover host</Space>}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select failover host.',
                                },
                            ]}
                        >
                            <Select>
                                {cluster
                                    ?.getDatabaseNodes()
                                    .filter(
                                        (node) =>
                                            !(
                                                node.port ===
                                                    +form.getFieldValue(
                                                        'port'
                                                    ) &&
                                                node.hostname ===
                                                    form.getFieldValue('host')
                                            )
                                    )
                                    .map((node) => (
                                        <Select.Option
                                            key={node?.getHostWithPort()}
                                            value={
                                                node?.getHostWithPort() || ''
                                            }
                                        >
                                            {getNodeHostWithDesc(node)}
                                        </Select.Option>
                                    ))}
                                <Select.Option key={'auto'} value={null}>
                                    Auto select
                                </Select.Option>
                            </Select>
                        </Form.Item>
                    }
                />
            );
        case 'verifyBackup':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="verifyBackup"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                />
            );
        case 'extendedInsert':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="extendedInsert"
                    label={
                        <Space>
                            {getBackupAdvancedFormFieldName(field)}
                            <InfoIcon
                                info={
                                    <span>
                                        Disable it so --skip-extended-insert
                                        --set-gtid-purged=OFF params will be set
                                        when performing mysqldump
                                    </span>
                                }
                            />
                        </Space>
                    }
                    valuePropName="checked"
                />
            );
        case 'wsrepDeSync':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="wsrepDeSync"
                    label={getBackupAdvancedFormFieldName('wsrepDeSync')}
                    valuePropName="checked"
                />
            );
        case 'xtrabackupBackupLocks':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="xtrabackupBackupLocks"
                    label={
                        <Space>
                            {getBackupAdvancedFormFieldName(field)}
                            <InfoIcon
                                info={
                                    <span>
                                        <b>
                                            {getBackupAdvancedFormFieldName(
                                                field
                                            )}
                                        </b>
                                        <br />
                                        Backup Locks set to "Yes". This is the
                                        default xtrabackup behavior and uses
                                        "LOCK TABLES FOR BACKUP" where it is
                                        supported when making a backup. Backup
                                        Locks set to "No" sets --no-backup-locks
                                        which use "FLUSH NO_WRITE_TO_BINLOG
                                        TABLES" and "FLUSH TABLES WITH READ
                                        LOCK" when making a backup.
                                    </span>
                                }
                            />
                        </Space>
                    }
                    valuePropName="checked"
                />
            );
        case 'xtrabackupLockDdlPerTable':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="xtrabackupLockDdlPerTable"
                    label={
                        <Space>
                            {getBackupAdvancedFormFieldName(field)}
                            <InfoIcon
                                info={
                                    <span>
                                        Sets --lock-ddl-per-table. Enable this
                                        flag if you have backup errors like
                                        `InnoDB: An optimized (without redo
                                        logging) DDLoperation has been
                                        performed.` This option is only applied
                                        to xtrabackup 2.4.8 and later version.
                                        Setting this flag may cause blocked
                                        updates to tables for highly loaded
                                        servers under some circumstances. Please
                                        see
                                        https://www.percona.com/blog/2017/08/08/avoiding-the-an-optimized-without-redo-logging-ddloperation-has-been-performed-error-with-percona-xtrabackup
                                        and
                                        https://jira.mariadb.org/browse/MDEV-14095
                                        and
                                        https://jira.mariadb.org/browse/MDEV-5336
                                    </span>
                                }
                            />
                        </Space>
                    }
                    valuePropName="checked"
                />
            );
        case 'xtrabackupParallellism':
            return (
                <Form.Item
                    name="xtrabackupParallellism"
                    label={
                        <Space>{getBackupAdvancedFormFieldName(field)}</Space>
                    }
                    valuePropName="checked"
                >
                    <Select
                        value={form.getFieldValue('xtrabackupParallellism')}
                        options={[
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 4, label: '4' },
                            { value: 8, label: '8' },
                            { value: 12, label: '12' },
                            { value: 16, label: '16' },
                        ]}
                    />
                </Form.Item>
            );
        case 'throttleRateNet':
            return (
                <Form.Item
                    name="throttleRateNet"
                    label={
                        <Space>
                            {getBackupAdvancedFormFieldName(field)}
                            <InfoIcon
                                info={
                                    <span>
                                        The "pv" tool will be used to throttle
                                        the streaming of the backup.
                                    </span>
                                }
                            />
                        </Space>
                    }
                >
                    <Input
                        value={form.getFieldValue('throttleRateNet') || 0}
                        type="number"
                    />
                </Form.Item>
            );
        case 'usePIGZ':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="usePIGZ"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                />
            );
        case 'useQPress':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="useQPress"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                />
            );
        case 'backupSystemDatabases':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name="backupSystemDatabases"
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                />
            );
        case 'dataStreamsAndIndexes':
        case 'allowPartialIndexes':
        case 'ignoreUnavailableIndexes':
        case 'includeGlobalState':
            return (
                <FormItemInlineSwitch
                    justify
                    noMargin
                    name={field}
                    label={getBackupAdvancedFormFieldName(field)}
                    valuePropName="checked"
                />
            );
        default:
            return <></>;
    }

    function getHostWithPort() {
        const port = form.getFieldValue('port');
        return `${form.getFieldValue('host')}${port ? ':' : ''}${port}`;
    }
}

export function getBackupAdvancedFormFieldName(field: string) {
    switch (field) {
        case 'compression':
            return 'Compression';
        case 'enableRetention':
            return 'Retention';
        case 'encryptBackup':
            return 'Enable encryption';
        case 'enablePartial':
            return 'Enable partial backup';
        case 'includeTables':
            return 'Include tables';
        case 'excludeTables':
            return 'Exclude tables';
        case 'splitDumpFiles':
            return 'One dump file per DB';
        case 'verifyBackup':
            return 'Verify backup';
        case 'failoverBackup':
            return 'Failover backup if node is down';
        case 'useQPress':
            return 'Use QPress compression';
        case 'compressionLevel':
            return 'Compression level';
        case 'customRetention':
            return 'Custom retention period (days)';
        case 'extendedInsert':
            return 'Use extended insert';
        case 'wsrepDeSync':
            return 'Desync node during backup';
        case 'xtrabackupBackupLocks':
            return 'Backup locks';
        case 'xtrabackupLockDdlPerTable':
            return 'Lock DDL per table';
        case 'xtrabackupParallellism':
            return 'Xtrabackup parallel copy threads';
        case 'throttleRateNet':
            return 'Network streaming throttle rate (MB/s)';
        case 'usePIGZ':
            return 'Use PIGZ for parallel gzip';
        case 'backupSystemDatabases':
            return 'Include system databases';
        case 'dataStreamsAndIndexes':
            return 'Data streams and indices';
        case 'allowPartialIndexes':
            return 'Allow partial indices';
        case 'ignoreUnavailableIndexes':
            return 'Ignore unavailable indices';
        case 'includeGlobalState':
            return 'Include global state';
        default:
            return '';
    }
}
